import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { upperFirst } from 'lodash-es';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

import { FeatureNames } from '@vi-iot/client/apis/operational-data/operational-data.constants';
import {
  CascadeSequenceProperties,
  CascadeSequenceStrategyCommands,
  CascadeSequenceStrategyProperties,
  Feature,
  FeatureDeprecated,
  LagDeviceBasicModel,
  RangeConstraints,
} from '@vi-iot/client/apis/operational-data/operational-data.model';

import {
  CascadeLagDeviceDetails,
  InstallationDetailsStore,
  InstallationFeaturesStore,
  isReady,
} from '@store';

import { FeatureValuePipe } from './../../../../operational-data-common/feature-pipes/feature-value.pipe';
import { SequencingStrategyForm } from './sequencing-strategy.helper';

enum SequenceModes {
  Fixed = 'fixedMode',
  FixedFirst = 'fixedFirst',
  FixedLast = 'fixedLast',
  BurnerRuntime = 'burnerRuntime',
}

@Component({
  selector: 'app-sequencing-strategy',
  templateUrl: './sequencing-strategy.component.html',
  styleUrls: ['./sequencing-strategy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SequencingStrategyComponent implements OnInit {
  SequenceModes = SequenceModes;
  sequenceStrategy$: Observable<
    FeatureDeprecated<CascadeSequenceStrategyProperties, CascadeSequenceStrategyCommands>
  >;

  cascadeSequence$: Observable<Feature<CascadeSequenceProperties, never>>;
  sequenceCombined$: Observable<any>;
  lagDevices$: Observable<LagDeviceBasicModel[]>;
  fixedFirstLagDevice: LagDeviceBasicModel;
  fixedLastLagDevice: LagDeviceBasicModel;
  formGroup: FormGroup<SequencingStrategyForm>;
  sequenceStates: { [key: string]: boolean } = {};

  get canExecuteCommands(): boolean {
    // Commands executions will be disabled until it will be blocked in the cloud layer
    return false;
  }

  get isBurnerRuntimeSaveButtonDisabled(): boolean {
    return (
      !this.canExecuteCommands ||
      this.formGroup.invalid ||
      !this.sequenceStates[SequenceModes.BurnerRuntime]
    );
  }
  constructor(
    private installationDetailsStore: InstallationDetailsStore,
    private featureValue: FeatureValuePipe,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.sequenceStrategy$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature(FeatureNames.cascadeSequenceStrategy)
      .pipe(
        filter((feature) => isReady(feature)),
        tap(
          (
            feature: FeatureDeprecated<
              CascadeSequenceStrategyProperties,
              CascadeSequenceStrategyCommands
            >
          ) => {
            const initialValue = '';
            const burnerRuntimeConstraints: RangeConstraints =
              feature?.commands?.activateBurnerRuntime?.params?.burnerRuntime?.constraints;
            this.formGroup = new FormGroup({
              burnerRuntimeValue: new FormControl<string>(initialValue, [
                Validators.required,
                Validators.min(burnerRuntimeConstraints?.min),
                Validators.max(burnerRuntimeConstraints?.max),
                Validators.pattern('^[0-9]+$'),
              ]),
            });
            if (!this.canExecuteCommands) {
              this.formGroup.disable();
            }

            Object.keys(SequenceModes).map((modeKey: string) => {
              const modeValue: string = SequenceModes[modeKey];
              this.sequenceStates[modeValue] = this.featureValue.transform(
                feature,
                modeValue
              ) as boolean;
            });
          }
        )
      );

    this.cascadeSequence$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.cascadeSequence
    );

    this.sequenceCombined$ = combineLatest([this.sequenceStrategy$, this.cascadeSequence$]).pipe(
      map(([strat, sequence]) => ({
        strat,
        sequence,
      }))
    );

    this.lagDevices$ = this.installationDetailsStore.getSequencedCascadeLagDevices$.pipe(
      map((devices: CascadeLagDeviceDetails[]) =>
        devices.map((device: CascadeLagDeviceDetails) => {
          const lagDevice: LagDeviceBasicModel = {
            id: device.id,
            name: device.type,
          };

          return lagDevice;
        })
      ),
      tap((devices: LagDeviceBasicModel[]) => {
        this.fixedFirstLagDevice = devices[0];
        this.fixedLastLagDevice = devices[devices.length - 1];
      })
    );
  }

  onSwitchChanged(mode: SequenceModes, isOn: boolean): void {
    if (isOn) {
      switch (mode) {
        case SequenceModes.Fixed:
          this.updateFeatureProperty(mode, true);
          break;
        case SequenceModes.FixedFirst:
          this.switchOnFixedFirstFeatureProperty();
          break;
        case SequenceModes.FixedLast:
          this.switchOnFixedLastFeatureProperty();
          break;
        case SequenceModes.BurnerRuntime:
          this.switchOnBurnerRuntimeFeatureProperty();
          break;
        default:
          break;
      }
    } else {
      this.updateFeatureProperty(mode, false);
    }
  }

  onFixedFirstLagDeviceChanged(): void {
    if (this.sequenceStates[SequenceModes.FixedFirst]) {
      this.switchOnFixedFirstFeatureProperty();
    }
  }

  onFixedLastLagDeviceChanged(): void {
    if (this.sequenceStates[SequenceModes.FixedLast]) {
      this.switchOnFixedLastFeatureProperty();
    }
  }

  onBurnerRuntimeSaveClicked(): void {
    this.switchOnBurnerRuntimeFeatureProperty();
  }

  compareLagDevices(device1: LagDeviceBasicModel, device2: LagDeviceBasicModel): boolean {
    return device1.name === device2.name && device1.id === device2.id;
  }

  private switchOnFixedFirstFeatureProperty(): void {
    this.updateFeatureProperty(
      SequenceModes.FixedFirst,
      true,
      'deviceName',
      this.fixedFirstLagDevice?.name
    );
  }

  private switchOnFixedLastFeatureProperty(): void {
    this.updateFeatureProperty(
      SequenceModes.FixedLast,
      true,
      'deviceName',
      this.fixedLastLagDevice?.name
    );
  }

  private switchOnBurnerRuntimeFeatureProperty(): void {
    const value: string = this.formGroup.controls.burnerRuntimeValue.value;
    this.updateFeatureProperty(SequenceModes.BurnerRuntime, true, 'burnerRuntime', value);
  }

  private updateFeatureProperty(
    mode: SequenceModes,
    isOn: boolean,
    paramName = '',
    value: any = null
  ): void {
    const propertyName: string = upperFirst(mode);
    const commandName = `${isOn ? 'activate' : 'deactivate'}${propertyName}`;

    this.installationFeaturesStore.updateFeature(
      FeatureNames.cascadeSequenceStrategy,
      value,
      mode,
      paramName,
      commandName
    );
  }
}
